/**
 * Google Analytics 4 — privacy-conscious, consent-gated.
 *
 * GA is NOT loaded until the visitor explicitly grants consent (deny-by-default),
 * so no Google cookies or requests happen for visitors who haven't accepted.
 * Consent is persisted in localStorage; `<CookieConsent>` collects it and
 * `<Analytics>` reacts to it (loads gtag, tracks SPA pageviews).
 *
 * Configure with VITE_GA_MEASUREMENT_ID (e.g. "G-XXXXXXXXXX"). If it's unset,
 * analytics are fully disabled — safe for local/dev builds.
 */

export const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

export const CONSENT_STORAGE_KEY = "cliqo.analytics-consent";
/** Dispatched on window when the consent decision changes. */
export const CONSENT_CHANGE_EVENT = "cliqo:consent-change";
export type ConsentChoice = "granted" | "denied";

// gtag pushes to this global queue; declared here for type-safety.
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let scriptLoaded = false;

/** Whether analytics are configured at all (measurement id present). */
export function isAnalyticsConfigured(): boolean {
  return Boolean(GA_MEASUREMENT_ID);
}

/** Read the stored consent decision, or null if the visitor hasn't chosen yet. */
export function getStoredConsent(): ConsentChoice | null {
  try {
    const v = localStorage.getItem(CONSENT_STORAGE_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

/** Persist the consent decision. */
export function setStoredConsent(choice: ConsentChoice): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch {
    /* localStorage unavailable (private mode, etc.) — ignore. */
  }
}

/** Persist a consent decision and notify listeners (e.g. <Analytics>). */
export function applyConsent(choice: ConsentChoice): void {
  setStoredConsent(choice);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(CONSENT_CHANGE_EVENT));
  }
}

/**
 * Inject the gtag.js script and initialise GA once. No-op if analytics aren't
 * configured or the script is already present. Only call after consent is
 * granted.
 */
export function loadGa(): void {
  if (!GA_MEASUREMENT_ID || scriptLoaded || typeof document === "undefined") {
    return;
  }
  scriptLoaded = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // gtag.js only recognises commands pushed as the raw `arguments` object.
  // Pushing a plain array (e.g. `push([...])` via rest params) makes gtag.js
  // treat the entry as generic data-layer content and silently ignore the
  // config/event command — the tag loads and "verifies", but no hits are ever
  // sent. So push `arguments` exactly as Google's canonical snippet does.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag("js", new Date());
  // We send page_view manually on route changes, so disable the automatic one.
  window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
}

/** Send a SPA page_view. Safe to call before gtag exists (queued / ignored). */
export function trackPageview(path: string): void {
  if (!GA_MEASUREMENT_ID || typeof window.gtag !== "function") {
    return;
  }
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}